import type React from "react";
import { useCallback } from "react";
import {
  insertNodesAtSelection,
  sanitizePastedHtml,
} from "@/lib/utils/editorPasteUtils";
import {
  getImageFromClipboard,
  isSupportedImageType,
} from "@/lib/utils/imageUtils";

interface UsePasteHandlerOptions {
  editorRef: React.RefObject<HTMLDivElement | null>;
  maxInlineImageCount: number;
  maxInlineImageTotalBytes: number;
  translate: (key: string, options?: Record<string, unknown>) => string;
  onImageError: (message: string | null) => void;
  onInlineImageAdd?: (file: File) => void;
  getCurrentAttachmentCount?: () => number;
  getCurrentAttachmentTotalBytes?: () => number;
  maxFileSize: number;
}

const MAX_IMAGE_DIMENSION = 800;

const resizeImageToAttachmentFile = (file: File): Promise<File> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      reject(new Error("Canvas context not available"));
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const cleanup = () => {
      URL.revokeObjectURL(objectUrl);
    };

    img.onload = () => {
      const { width, height } = img;
      let newWidth = width;
      let newHeight = height;

      if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
        if (width >= height) {
          newWidth = MAX_IMAGE_DIMENSION;
          newHeight = (height * MAX_IMAGE_DIMENSION) / width;
        } else {
          newHeight = MAX_IMAGE_DIMENSION;
          newWidth = (width * MAX_IMAGE_DIMENSION) / height;
        }
      }

      canvas.width = newWidth;
      canvas.height = newHeight;
      ctx.drawImage(img, 0, 0, newWidth, newHeight);

      const isPng = file.type === "image/png";
      const isWebp = file.type === "image/webp";

      const outputType = isPng
        ? "image/png"
        : isWebp
          ? "image/webp"
          : "image/jpeg";
      const outputExtension = isPng ? "png" : isWebp ? "webp" : "jpg";
      const quality = outputType === "image/jpeg" ? 0.8 : undefined;

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            cleanup();
            reject(new Error("Failed to create blob from canvas"));
            return;
          }

          const originalName = file.name;
          const baseName = originalName
            ? originalName.replace(/\.[^/.]+$/, "")
            : "pasted-image";
          const timestamp = Date.now();

          const fileName = `${baseName || "pasted-image"}-${timestamp}.${outputExtension}`;
          const resizedFile = new File([blob], fileName, { type: outputType });
          cleanup();
          resolve(resizedFile);
        },
        outputType,
        quality,
      );
    };

    img.onerror = () => {
      cleanup();
      reject(new Error("Failed to load image"));
    };

    img.src = objectUrl;
  });
};

export const usePasteHandler = ({
  editorRef,
  maxInlineImageCount,
  maxInlineImageTotalBytes,
  translate,
  onImageError,
  onInlineImageAdd,
  getCurrentAttachmentCount,
  getCurrentAttachmentTotalBytes,
  maxFileSize,
}: UsePasteHandlerOptions) => {
  const dispatchInputEvent = useCallback(() => {
    if (!editorRef.current) return;
    const inputEvent = new Event("input", { bubbles: true });
    editorRef.current.dispatchEvent(inputEvent);
  }, [editorRef]);

  const handleImagePaste = useCallback(
    async (imageFile: File, event: React.ClipboardEvent<HTMLDivElement>) => {
      const currentAttachmentCount = getCurrentAttachmentCount?.() ?? 0;
      if (currentAttachmentCount >= maxInlineImageCount) {
        onImageError(
          translate("common.imageCountLimitError", {
            count: maxInlineImageCount,
          }),
        );
        event.preventDefault();
        return;
      }

      event.preventDefault();
      onImageError(null);

      if (!isSupportedImageType(imageFile.type)) {
        onImageError(translate("common.imageExtensionError"));
        return;
      }

      if (imageFile.size > maxFileSize) {
        onImageError(
          translate("common.imageSizeError", {
            size: (maxFileSize / 1024 / 1024).toFixed(0),
          }),
        );
        return;
      }

      try {
        const existingBytes = getCurrentAttachmentTotalBytes?.() ?? 0;
        const resizedFile = await resizeImageToAttachmentFile(imageFile);

        if (existingBytes + resizedFile.size > maxInlineImageTotalBytes) {
          onImageError(
            translate("common.imageTotalSizeLimitError", {
              size: maxInlineImageTotalBytes / 1024 / 1024,
            }),
          );
          return;
        }

        onInlineImageAdd?.(resizedFile);
      } catch (error) {
        console.error("Image paste error:", error);
        onImageError(translate("common.imageProcessingError"));
      }
    },
    [
      getCurrentAttachmentCount,
      getCurrentAttachmentTotalBytes,
      maxInlineImageCount,
      maxInlineImageTotalBytes,
      onImageError,
      onInlineImageAdd,
      translate,
      maxFileSize,
    ],
  );

  const handleTextInsertion = useCallback(
    (text: string) => {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        const textNode = document.createTextNode(text);
        range.insertNode(textNode);
        range.setStartAfter(textNode);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      } else if (editorRef.current) {
        editorRef.current.appendChild(document.createTextNode(text));
      }

      dispatchInputEvent();
    },
    [dispatchInputEvent, editorRef],
  );

  const handleHtmlInsertion = useCallback(
    (htmlContent: string) => {
      const sanitizedNodes = sanitizePastedHtml(htmlContent);
      insertNodesAtSelection(sanitizedNodes, editorRef.current);
      dispatchInputEvent();
    },
    [dispatchInputEvent, editorRef],
  );

  const handlePaste = useCallback(
    async (event: React.ClipboardEvent<HTMLDivElement>) => {
      const imageFile = getImageFromClipboard(event.nativeEvent);
      if (imageFile) {
        await handleImagePaste(imageFile, event);
        return;
      }

      const { clipboardData } = event;
      if (!clipboardData) {
        return;
      }

      const htmlContent = clipboardData.getData("text/html");
      if (htmlContent) {
        event.preventDefault();
        handleHtmlInsertion(htmlContent);
        return;
      }

      const plainText = clipboardData.getData("text/plain");
      if (!plainText) {
        return;
      }

      event.preventDefault();
      handleTextInsertion(plainText);
    },
    [handleHtmlInsertion, handleImagePaste, handleTextInsertion],
  );

  return {
    handlePaste,
  };
};
